import { createSelector } from '@ngrx/store';
import { AppState } from '../app.reducer';
import { IPrepaidRoomClusterGroup } from '@core/models/PrepaidRoomClusterGroup/IPrepaidRoomCluster';
import { IStateDisneyTickets } from '@store/disney-tickets/disneyTickets.reducer';
import { IDisneyClusterTickets } from '@core/models/modals';
import { Rate } from '@core/models/Rate/Rate.model';
import { MealPlan } from '../../shared/enums/meal-plan.enum';
import { Supplements } from '@core/models/Supplement/Supplements.model';

const selectFeature = (state: AppState) => state.ticketsState;

export const selectTicketsState = createSelector(
  selectFeature,
  (state: IStateDisneyTickets) => state.disneyTicketsCluster,
);

export const selectCluster = createSelector(
  selectTicketsState,
  (state: IDisneyClusterTickets) => state.cluster,
);

export const selectRoomName = createSelector(
  selectTicketsState,
  (state: IDisneyClusterTickets) => state.nameRoom,
);

export const selectRoomPricePerNight = createSelector(
  selectTicketsState,
  (state: IDisneyClusterTickets) => state.roomPricePerNight,
);

export const selectMealPlans = createSelector(
  selectCluster,
  (cluster: IPrepaidRoomClusterGroup) => {
    if (cluster && cluster.rates.length > 0) {
      const rates = cluster.rates.filter(
        (rate: Rate) => rate.meal_plan.code !== MealPlan.ROOM_ONLY,
      );
      return Array.from(new Set<string>(rates.map((rate) => rate.meal_plan.description)));
    }
    return [];
  },
);

export const selectTicketsMap = createSelector(
  selectFeature,
  (state: IStateDisneyTickets) => state.ticketsMap,
);

export const selectTicketsByDay = createSelector(
  selectTicketsMap,
  (ticketsMap, props) => {
    return ticketsMap
      .get(props.days)
      .filter((ticket: Supplements) => ticket.ticket !== '')
      .reduce((acc, current) => {
        const isDuplicate = acc.some(
          (supplement) => supplement.ticket === current.ticket,
        );
        if (!isDuplicate) {
          acc.push(current);
        }
        return acc;
      }, [])
      .sort(
        (ticket1: Supplements, ticket2: Supplements) => ticket1.total_amount - ticket2.total_amount,
      );
  },
);

export const selectRatesByTicket = createSelector(
  selectFeature,
  (state) => state.ratesByTicket,
);

export const selectMealPlansByTicket = createSelector(selectRatesByTicket, (state) =>
  state.filter((rate) => rate.meal_plan.code !== MealPlan.ROOM_ONLY),
);

export const selectIsTicketSelected = createSelector(
  selectFeature,
  (state) => state.ticketSelected !== null,
);

export const selectTicketSelected = createSelector(
  selectFeature,
  (state) => state.ticketSelected,
);

export const selectRateSelected = createSelector(
  selectFeature,
  (state) => state.rateSelected,
);

export const selectIsRateSelected = createSelector(
  selectFeature,
  (state) => state.rateSelected !== null,
);

export const selectStartDate = createSelector(selectFeature, (state) => state.startDate);

export const selectRateCheckInProgress = createSelector(
  selectFeature,
  (state) => state.rateCheckInProgress,
);

export const selectCheckRateFailed = createSelector(
  selectFeature,
  (state) => state.checkRateFail,
);

export const selectRateToCheck = createSelector(
  selectRateSelected,
  selectTicketSelected,
  selectStartDate,
  (rateSelected, ticketSelected, startDate) => ({
    rateSelected,
    ticketSelected,
    startDate,
  }),
);

export const selectRateValidForReservation = createSelector(
  selectRateSelected,
  selectIsTicketSelected,
  (rateSelected, isTicketSelected) => {
    if (
      rateSelected.meal_plan.code === MealPlan.ROOM_ONLY &&
      (rateSelected.supplements === undefined || rateSelected.supplements.length === 0)
    ) {
      return true;
    }

    return rateSelected.supplements.length > 0 && isTicketSelected;
  },
);
